/**
 * DAY BY DAY — the doing layer, folded into Project where a candidate can find it.
 *
 * FOUNDER, 2026-08-26: "scratch by mit was interactive and people learnt, wondering our code101,
 * aicode101, claudecode101 shall give value to user as if they are learning with fun."
 *
 * Then, 2026-08-27, on discovering it had shipped as its own page: fold it in here.
 *
 * ── WHY IT MOVED ───────────────────────────────────────────────────────────────────────────────
 * It was built as project.html at /project and was reachable from NOWHERE in the shell. A second
 * surface called Project is worse than a second URL: tapping Project gave you the certificate track
 * while the journal you were told to keep sat at an address nobody hands out. The founder's standing
 * rule is that every capability flows from one page, and a capability nobody can reach is not a
 * capability at all — the comment at the top of projectsurface.jsx records this exact fault being
 * found once already, for P1/P2/P4, by the founder asking where to click.
 *
 * ── WHAT IT IS, AND IS NOT ─────────────────────────────────────────────────────────────────────
 * A JOURNAL, NOT A QUIZ. Nothing here is marked and nothing is sent anywhere. The questions are
 * lifted verbatim from the course sources by project-days.mjs, which fails rather than paraphrase,
 * because a question a machine invented about your project is worth nothing to you.
 *
 * WHAT YOU WRITE STAYS IN THIS BROWSER. localStorage, keyed by track and day, and the page says so
 * out loud rather than letting anyone assume a server has it. That is a real limitation — clear the
 * browser and it is gone — and stating it is the honest alternative to implying a safety that does
 * not exist. Every read and write is wrapped, because a private window can throw on access alone.
 */

import { useState, useEffect } from 'react';
import { API } from './surfaces.jsx';

const KEY = (track, n) => `ankr.journal.${track}.${n}`;
const read = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const write = (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k); } catch { /* private window */ } };

export default function JournalLab({ level }) {
  const track = String(level || '').toLowerCase();
  const [days, setDays] = useState(null);
  const [open, setOpen] = useState(1);
  const [text, setText] = useState('');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setDays(null); setErr(null);
    /* ${API}/api/…, which is what every other fetch in this app uses. project.html defined its own
       API constant that already ENDED in /api, and carrying that convention across made this the one
       call in the bundle missing the segment — so the part rendered, empty, with no error: a 404 on a
       fetch is a resolved promise, not a throw. */
    fetch(`${API}/api/project?track=${encodeURIComponent(track)}`)
      .then((r) => r.json())
      .then((d) => { if (alive) setDays(d?.days || d?.entries || (Array.isArray(d) ? d : [])); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [track]);

  useEffect(() => { setText(read(KEY(track, open))); }, [track, open]);

  if (err) return <p className="g-dim">The day-by-day questions could not be loaded: {err}</p>;
  if (!days) return <p className="g-dim">Loading…</p>;
  if (!days.length) {
    return (
      <p className="g-dim">
        This ladder has no project days yet. That is an empty shelf, not a broken page — the days are
        lifted from the course source, and nothing is invented to fill the gap.
      </p>
    );
  }

  const day = days.find((d) => Number(d.day ?? d.n) === Number(open)) || days[0];
  const questions = day?.questions || [];

  return (
    <div className="prs-journal">
      <div className="lab-task-head prs-nav">
        <span className="g-label">Day</span>
        {days.map((d) => {
          const n = Number(d.day ?? d.n);
          const written = !!read(KEY(track, n));
          return (
            <button key={n} className={`g-btn ${Number(open) === n ? 'g-btn-primary' : ''}`}
              title={d.heading || ''} onClick={() => setOpen(n)}>
              {n}{written ? ' ·' : ''}
            </button>
          );
        })}
      </div>

      {day?.heading && <h3 className="g-h3">{day.heading}</h3>}
      {day?.module && <p className="g-label">{day.module}</p>}
      {day?.intro && <p className="prs-journal-intro">{day.intro}</p>}

      <ol className="prs-qs">
        {questions.map((q, i) => <li key={q.n ?? i}>{typeof q === 'string' ? q : q.text}</li>)}
      </ol>

      {/* What the day asks you to hand in, in the source's own words — stated before the box rather
          than after it, because a learner should know what he is writing towards. */}
      {day?.submit && <p className="g-dim">What this day produces: {day.submit}</p>}

      <label className="g-label" htmlFor="journal-entry">Your answer, for you</label>
      <textarea id="journal-entry" className="g-input prs-journal-box" rows={10} value={text}
        placeholder="Write as much or as little as you like. Nobody marks this."
        onChange={(e) => { setText(e.target.value); write(KEY(track, Number(open)), e.target.value); }} />
      <p className="g-dim prs-journal-note">
        Kept in this browser only — not sent anywhere, and not backed up. Clearing your browser data
        clears this with it.
      </p>
    </div>
  );
}
